import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Folder, FileText, ChevronRight, Download } from 'lucide-react';
import integrationsService from '../../services/integrationsService';
import Spinner from './Spinner';
import { formatDateTime } from '../../utils/datetime';
import { apiErrorMessage } from '../../utils/apiError';
import './MqQueueModal.css';
import './FileViewerModal.css';

// WHAT A TARGET THAT STORES IS HOLDING — the FILE_VIEWER result viewer (V583, marketplace.result_viewer).
//
// Azure Blob Storage answers a write with "stored" and nothing else, so the only way to see what a pipeline
// delivered is to look in the container. This browses it one folder at a time (sub-folders and files, a page at a
// time with Load more) and shows a file's content — pretty-printed when it is JSON, the first megabyte when it is
// bigger. Read-only.
//
// Opened from the Target card on an implementation's board (IntegrationDesigner), so it reads THAT property's
// binding — its own credential and container. The sibling of MqQueueModal, which is the same idea for a target
// that collects rather than stores.

const formatBytes = (n) => {
    if (!n && n !== 0) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};
const baseName = (path) => String(path || '').replace(/\/$/, '').split('/').pop();
const pretty = (text) => {
    try { return JSON.stringify(JSON.parse(text), null, 2); } catch (e) { return text; }
};

const FileViewerModal = ({ isOpen, onClose, integrationId, accessId, systemName, propertyName }) => {
    const [prefix, setPrefix] = useState('');
    const [folder, setFolder] = useState(null);
    const [where, setWhere] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [file, setFile] = useState(null);
    const [fileError, setFileError] = useState(null);
    const [fileLoading, setFileLoading] = useState(false);

    const load = useCallback(async (at, marker = null) => {
        if (!integrationId || !accessId) return;
        setLoading(true);
        setError(null);
        try {
            const data = await integrationsService.getFiles(integrationId, accessId, { prefix: at, marker });
            setWhere(data.where || null);
            setFolder(prev => (marker && prev
                ? { ...data, folders: [...prev.folders, ...data.folders], files: [...prev.files, ...data.files] }
                : data));
        } catch (e) {
            setError(apiErrorMessage(e, 'The files could not be listed'));
            if (!marker) setFolder(null);
        } finally {
            setLoading(false);
        }
    }, [integrationId, accessId]);

    useEffect(() => { if (isOpen) { setFile(null); load(prefix); } }, [isOpen, prefix, load]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    const open = async (name) => {
        setFileLoading(true);
        setFileError(null);
        setFile({ name });
        try {
            const data = await integrationsService.getFiles(integrationId, accessId, { name });
            setFile(data.file);
        } catch (e) {
            setFileError(apiErrorMessage(e, 'The file could not be read'));
        } finally {
            setFileLoading(false);
        }
    };

    const download = () => {
        if (!file || (!file.text && !file.base64)) return;
        const blob = file.text != null
            ? new Blob([file.text], { type: file.contentType || 'text/plain' })
            : new Blob([Uint8Array.from(atob(file.base64), c => c.charCodeAt(0))], { type: file.contentType || 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = baseName(file.name);
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    if (!isOpen) return null;

    // The folders above this one, as breadcrumbs: '' (the container), 'a/', 'a/b/' …
    const crumbs = [''].concat(prefix.split('/').filter(Boolean).map((_, i, parts) => `${parts.slice(0, i + 1).join('/')}/`));

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content acm-modal fvm" onClick={e => e.stopPropagation()}
                role="dialog" aria-modal="true" aria-labelledby="fvm-title">
                <div className="modal-header">
                    <h2 id="fvm-title">
                        Files in {systemName || 'this target'}
                        {propertyName && <span className="acm-hint"> · {propertyName}</span>}
                    </h2>
                    <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
                </div>

                <div className="modal-body">
                    <p className="acm-hint mqm-lede">
                        What this property's pipeline has stored{where ? <> in <code>{where}</code></> : ''}, read
                        with its own credential. Read-only.
                    </p>

                    <div className="mqm-tally">
                        <nav className="fvm-crumbs" aria-label="Folder">
                            {crumbs.map((c, i) => (
                                <React.Fragment key={c || 'root'}>
                                    {i > 0 && <ChevronRight size={12} />}
                                    <button type="button" className="fvm-crumb" disabled={c === prefix}
                                        onClick={() => setPrefix(c)}>
                                        {c ? baseName(c) : 'container'}
                                    </button>
                                </React.Fragment>
                            ))}
                        </nav>
                        <span className="mqm-grow" />
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => load(prefix)} disabled={loading}>
                            <RefreshCw size={14} /> Refresh
                        </button>
                    </div>

                    {error && <div className="os-msg os-msg-error">{error}</div>}

                    <div className="fvm-panes">
                        <div className="fvm-list">
                            {loading && !folder ? <div className="loading-section"><Spinner /></div> : folder && (
                                <>
                                    {!folder.folders.length && !folder.files.length && (
                                        <p className="mqm-empty">This folder is empty.</p>
                                    )}
                                    <table className="acp-table">
                                        <tbody>
                                            {folder.folders.map(f => (
                                                <tr key={f} className="fvm-row" onClick={() => setPrefix(f)}>
                                                    <td><Folder size={14} /> {baseName(f)}/</td>
                                                    <td /><td />
                                                </tr>
                                            ))}
                                            {folder.files.map(f => (
                                                <tr key={f.name} className={`fvm-row${file && file.name === f.name ? ' is-open' : ''}`}
                                                    onClick={() => open(f.name)}>
                                                    <td title={f.name}><FileText size={14} /> {baseName(f.name)}</td>
                                                    <td className="mqm-muted">{formatBytes(f.size)}</td>
                                                    <td className="mqm-muted">{f.lastModified ? formatDateTime(f.lastModified) : ''}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {folder.nextMarker && (
                                        <button type="button" className="btn btn-secondary btn-sm fvm-more"
                                            onClick={() => load(prefix, folder.nextMarker)} disabled={loading}>
                                            Load more
                                        </button>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="fvm-preview">
                            {!file ? (
                                <p className="mqm-empty">Choose a file to see its content.</p>
                            ) : (
                                <>
                                    <div className="fvm-preview-head">
                                        <strong title={file.name}>{baseName(file.name)}</strong>
                                        <span className="mqm-muted">{formatBytes(file.size)}{file.contentType ? ` · ${file.contentType}` : ''}</span>
                                        <span className="mqm-grow" />
                                        {(file.text != null || file.base64) && (
                                            <button type="button" className="btn btn-secondary btn-sm" onClick={download}>
                                                <Download size={14} /> Download
                                            </button>
                                        )}
                                    </div>
                                    {fileError && <div className="os-msg os-msg-error">{fileError}</div>}
                                    {fileLoading ? <div className="loading-section"><Spinner /></div> : (
                                        <>
                                            {file.truncated && (
                                                <div className="os-msg os-msg-warn">
                                                    Showing the first 1 MB of {formatBytes(file.size)}.
                                                </div>
                                            )}
                                            {file.text != null && <pre className="fvm-content">{pretty(file.text)}</pre>}
                                            {file.base64 && (
                                                <p className="mqm-empty">Binary content — use Download to open it.</p>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
};

export default FileViewerModal;
